<template>
	<div>
		<v-card-title v-if="title" class="d-flex align-center">
			{{ title }}
			<v-icon color="info" @click="load">$reload</v-icon>
		</v-card-title>
		<v-card-title>
			<v-form @submit.prevent="search" class="form-inline">
				<v-row>
					<v-col cols="12" class="d-flex flex-wrap align-center">
						<v-select
							outlined
							class="mr-2"
							dense
							label="杯测方法"
							v-model="query.method"
							:items="refs['CuppingMethod']"
							item-title="title"
							item-value="value"
							hide-details
							clearable
							style="max-width: 12rem"
						></v-select>
						<v-chip
							v-if="query.cupping_id"
							class="mr-2"
							size="small"
							closable
							color="primary"
							variant="tonal"
							@click:close="clearFilter('cupping_id')"
						>
							杯测 #{{ query.cupping_id }}
						</v-chip>
						<v-chip
							v-if="query.bean_id"
							class="mr-2"
							size="small"
							closable
							color="secondary"
							variant="tonal"
							@click:close="clearFilter('bean_id')"
						>
							生豆 #{{ query.bean_id }}
						</v-chip>
						<v-btn color="primary" size="large" class="mr-2" @click="search">查询</v-btn>
						<v-icon class="mr-2" v-if="queryPairs.length" @click="reset">$close</v-icon>
					</v-col>
				</v-row>
			</v-form>
		</v-card-title>

		<v-data-table-server
			:headers="headers"
			:items="objects"
			@update:options="load"
			:items-length="parseInt(total)"
			:loading="loading"
			:show-select="showSelect"
			:single-select="singleSelect"
			v-model="selected"
			multi-sort
		>
			<template v-slot:top>
				<slot name="batchActions" v-bind:selected="selected"></slot>
			</template>
			<template #item.bean_id="{ item }">
				{{ item.bean?.name || '—' }}
			</template>
			<template #item.cupping_id="{ item }">
				<template v-if="item.cupping">
					{{ item.cupping.location || '杯测' }}（{{ item.cupping.id }}）
				</template>
				<span v-else class="text-medium-emphasis">—</span>
			</template>
			<template #item.method="{ item }">
				<enum-chip kind="cuppingMethod" :value="item.method" />
			</template>
			<template #item.brew="{ item }">
				<div>{{ item.beanqty || '—' }}g · {{ item.watertemp || '—' }}°C</div>
				<div class="text-medium-emphasis">{{ grindLabel(item) }}</div>
			</template>
			<template #item.scores="{ item }">
				{{ scoreText(item) }}
			</template>
			<template #item.created_at="{ item }">
				{{ $formatTime(item.created_at) }}
			</template>
			<template v-slot:item.action="{ item }">
				<slot name="actions" v-bind="{ item }"></slot>
			</template>
			<template v-slot:footer>
				<slot name="batchActions" v-bind="{ selected }"></slot>
			</template>
		</v-data-table-server>
	</div>
</template>

<script>
import { clone, valuesIn, toPairsIn, filter, intersection } from 'lodash-es'
import qs from 'qs'
import EnumChip from '../common/EnumChip.vue'

export default {
	name: 'CuppingSampleList',
	components: { EnumChip },
	props: {
		initQuery: { type: Object, default: null },
		title: { type: String, default: null },
		showSelect: { type: Boolean, default: false },
		singleSelect: { type: Boolean, default: true },
		headers: {
			type: Array,
			default: () => [
				{ title: '#', value: 'id' },
				{ title: '豆子', align: 'left', sortable: true, value: 'bean_id' },
				{ title: '杯测', align: 'left', sortable: true, value: 'cupping_id' },
				{ title: '方法', value: 'method' },
				{ title: '冲煮', sortable: false, value: 'brew' },
				{ title: '评分', sortable: false, value: 'scores' },
				{ title: '时间', sortable: true, value: 'created_at' },
				{ title: '', align: 'left', sortable: false, value: 'action' }
			]
		}
	},
	data() {
		const qps = qs.parse(location.search, { ignoreQueryPrefix: true })
		return {
			query: qps,
			currentQuery: {},
			objects: [],
			total: 0,
			loading: false,
			options: null,
			refs: {},
			selected: [],
			errors: {}
		}
	},
	mounted() {
		if (this.initQuery) this.query = clone(this.initQuery)
		this.$api.common.meta('cupping', 'CuppingMethod', (response) => {
			this.refs = response
		})
		this.load()
	},
	computed: {
		queryValues() {
			return filter(valuesIn(this.query), (e) => !!e)
		},
		currentQueryValues() {
			return filter(valuesIn(this.currentQuery), (e) => !!e)
		},
		queryPairs() {
			return filter(toPairsIn(this.query), (e) => !!e[1])
		}
	},
	methods: {
		grindLabel(item) {
			const size = item?.grindsize || ''
			const name = item?.grinder_name || ''
			const text = name ? `${name} ${size}`.trim() : size
			return text || '—'
		},
		scoreText(item) {
			const scores = item?.scores || []
			if (!scores.length) return '未评分'
			const sum = scores.reduce((acc, score) => acc + Number(score.total || 0), 0)
			return `${scores.length}次 · ${(sum / scores.length).toFixed(1)}`
		},
		search() {
			if (
				this.queryValues.length !== this.currentQueryValues.length ||
				intersection(this.queryValues, this.currentQueryValues).length !== this.queryValues.length
			) {
				if (this.options?.page === 1) this.load()
				else if (this.options) this.options.page = 1
				else this.load()
			} else {
				this.load()
			}
		},
		reset() {
			this.query = {}
		},
		clearFilter(key) {
			delete this.query[key]
			this.search()
		},
		load(options = null) {
			if (!this.options) this.options = {}
			if (options) this.options = options
			let { sortBy, page, itemsPerPage } = this.options
			this.loading = true
			let qwp = clone(this.query)
			qwp.pageno = page
			qwp.pagesize = itemsPerPage
			qwp.sortBy = 'id'
			qwp.sortDesc = 'desc'
			if (sortBy && sortBy.length > 0) {
				qwp.sort = sortBy.map((e) => `${e.key} ${e.order}`).join(',')
				qwp.sortBy = sortBy[0]['key']
				qwp.sortDesc = sortBy[0]['order']
			}
			this.currentQuery = clone(this.query)
			this.$api.cupping.searchSample(
				qwp,
				(response) => {
					this.objects = response.objects
					this.total = response.total
					this.loading = false
				},
				(error) => {
					this.errors = error.message
					this.loading = false
				}
			)
		}
	}
}
</script>
